/**
 * Search Console palette tokens (spec §0d).
 *
 * The spec states its palette is a default, not a mandate — but the direction and
 * verdict colours are paired with a glyph and a text label everywhere they are used
 * (spec §9 a11y: never colour alone), so they are kept verbatim rather than remapped
 * onto the dashboard's emerald/red, which carries a stronger "good/bad" charge than
 * "declined" deserves on a report an owner reads monthly.
 */

/** Direction of a change. `neutral` also covers "not comparable". */
export const DIRECTION = {
  improved: '#22C55E',
  declined: '#F97316',
  neutral: '#94A3B8',
};

/** Chart series. Kept distinct in lightness as well as hue for greyscale/print. */
export const SERIES = {
  impressions: '#94A3B8',
  clicks: '#3B82F6',
  brand: '#8B5CF6',
  discovery: '#22C55E',
  opportunity: '#EAB308',
  currentYear: '#3B82F6',
  priorYear: '#94A3B8',
  yearBefore: '#CBD5E1',
};

/** Index-inspection verdicts. Informational only — never alert styling (F12). */
export const VERDICT = {
  PASS: '#22C55E',
  NEUTRAL: '#EAB308',
  FAIL: '#EF4444',
  UNKNOWN: '#94A3B8',
};

/** Colour for an inspection verdict, including the `null` verdict Google returns. */
export const verdictColor = (verdict) => VERDICT[verdict] || VERDICT.UNKNOWN;

/** Year-series colour by position in `summary.years` (most recent first). */
export const yearColor = (index) =>
  [SERIES.currentYear, SERIES.priorYear, SERIES.yearBefore][index] || SERIES.yearBefore;

/**
 * Tints for the health-panel stat icons.
 *
 * ── Why these four, and not the obvious ones ────────────────────────────────────
 * Every semantic hue is already spoken for on this page, and an icon repeating one reads
 * as a claim about the figure beside it:
 *
 *   green   #22C55E  `DIRECTION.improved`, `VERDICT.PASS`, `SERIES.discovery`
 *   amber   #EAB308  `VERDICT.NEUTRAL`, `SERIES.opportunity`
 *   red     #EF4444  `VERDICT.FAIL`
 *   orange  #F97316  `DIRECTION.declined`
 *   blue    #32B1FF  every link, button and accent on the page
 *   slate   #94A3B8  `SERIES.impressions`, `DIRECTION.neutral`
 *   teal / sky / indigo        the four `MetricTile` accents on the headline card
 *
 * These four appear nowhere else, so the icons read as a set of markers rather than as
 * verdicts competing with the PASS / FAIL / NEUTRAL dots and chips directly beneath them.
 *
 * ── What this gives up ──────────────────────────────────────────────────────────
 * The severity reading. A green "Google can show" over a red "Different address chosen"
 * told the reader which figures were good news; this does not. That is the trade the
 * product owner chose after seeing both, and it also puts the page back inside §12
 * ("Alert styling. Nothing red, nothing urgent"), which the semantic version departed
 * from. Swapping back is a one-line change to the `tone` props in `HealthSummary`.
 */
export const ICON_TINTS = {
  // `fg` tints the glyph, `bg` fills the square behind it, `border` outlines the whole
  // tile and `panel` washes the tile body — the four are one set so a tone can never be
  // half-applied. `panel` is fainter than `bg` (the icon square) so the square still
  // reads as a distinct object on top of the tinted card rather than dissolving into it.
  cyan: {
    fg: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-500/10 dark:bg-cyan-500/15',
    border: 'border-cyan-500/30 dark:border-cyan-500/40',
    panel: 'bg-cyan-500/[0.04] dark:bg-cyan-500/10',
  },
  violet: {
    fg: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-500/10 dark:bg-violet-500/15',
    border: 'border-violet-500/30 dark:border-violet-500/40',
    panel: 'bg-violet-500/[0.04] dark:bg-violet-500/10',
  },
  fuchsia: {
    fg: 'text-fuchsia-600 dark:text-fuchsia-400',
    bg: 'bg-fuchsia-500/10 dark:bg-fuchsia-500/15',
    border: 'border-fuchsia-500/30 dark:border-fuchsia-500/40',
    panel: 'bg-fuchsia-500/[0.04] dark:bg-fuchsia-500/10',
  },
  pink: {
    fg: 'text-pink-600 dark:text-pink-400',
    bg: 'bg-pink-500/10 dark:bg-pink-500/15',
    border: 'border-pink-500/30 dark:border-pink-500/40',
    panel: 'bg-pink-500/[0.04] dark:bg-pink-500/10',
  },
  // The remaining three, for `data_coverage`. Same rule: none of them appears anywhere
  // else on the page, so no icon can be mistaken for a verdict.
  teal: {
    fg: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-500/10 dark:bg-teal-500/15',
    border: 'border-teal-500/30 dark:border-teal-500/40',
    panel: 'bg-teal-500/[0.04] dark:bg-teal-500/10',
  },
  rose: {
    fg: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10 dark:bg-rose-500/15',
    border: 'border-rose-500/30 dark:border-rose-500/40',
    panel: 'bg-rose-500/[0.04] dark:bg-rose-500/10',
  },
  stone: {
    fg: 'text-stone-600 dark:text-stone-400',
    bg: 'bg-stone-500/10 dark:bg-stone-500/15',
    border: 'border-stone-500/30 dark:border-stone-500/40',
    panel: 'bg-stone-500/[0.04] dark:bg-stone-500/10',
  },
};
